// Deterministic, instant ATS-readiness check computed entirely in the browser
// (no AI, no network). It mirrors the server's ATS rubric for the categories
// that do not need a job description, so it can run live as the user edits and
// stays free for everyone (the job-specific match analysis stays Pro).

import { resumeToPlainText } from "./job-match";

const ACTION_VERBS = [
  "led",
  "built",
  "designed",
  "developed",
  "implemented",
  "managed",
  "created",
  "launched",
  "improved",
  "increased",
  "reduced",
  "delivered",
  "optimized",
  "automated",
  "architected",
  "migrated",
  "shipped",
  "drove",
  "owned",
  "scaled",
  "mentored",
  "coordinated",
  "achieved",
  "spearheaded",
  "streamlined",
  "negotiated",
  // French action verbs (past participles), so a French resume scores fairly.
  "dirigé",
  "géré",
  "piloté",
  "encadré",
  "coordonné",
  "supervisé",
  "conçu",
  "développé",
  "créé",
  "réalisé",
  "déployé",
  "lancé",
  "construit",
  "amélioré",
  "optimisé",
  "augmenté",
  "réduit",
  "accéléré",
  "livré",
  "généré",
  "automatisé",
  "transformé",
  "négocié",
  "obtenu",
  "atteint",
  "dépassé",
  "formé",
  "recruté",
  "animé",
  "mené",
  "initié",
  "orchestré",
  "sécurisé",
  "migré",
  "intégré",
  "planifié",
  "analysé",
  "assuré",
  "assumé",
  "établi",
  "élaboré",
  "défini",
  "proposé",
  "présenté",
  "rédigé",
  "effectué",
  "exécuté",
  "traité",
  "résolu",
  "corrigé",
  "testé",
  "validé",
  "vérifié",
  "contrôlé",
  "audité",
  "évalué",
  "suivi",
  "accompagné",
  "conduit",
  "conseillé",
  "organisé",
  "préparé",
  "budgétisé",
  "chiffré",
  "facturé",
  "vendu",
  "prospecté",
  "fidélisé",
  "configuré",
  "installé",
  "paramétré",
  "administré",
  "maintenu",
  "modernisé",
  "restructuré",
  "rationalisé",
  "standardisé",
  "centralisé",
  "digitalisé",
  "programmé",
  "modélisé",
  "fiabilisé",
  "monté",
  "refondu",
  "harmonisé",
];

const clamp = (n: number): number => Math.max(0, Math.min(100, Math.round(n)));

const escapeRegExp = (value: string): string =>
  value.replace(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);

const hasKeyword = (loweredText: string, keyword: string): boolean => {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return false;
  return new RegExp(`(^|[^a-z0-9+#.])${escapeRegExp(kw)}([^a-z0-9+#.]|$)`).test(loweredText);
};

export type ReadinessStats = {
  score: number;
  wordCount: number;
  metrics: number;
  actionVerbs: number;
  hasEmail: boolean;
  hasPhone: boolean;
  hasExperience: boolean;
  hasEducation: boolean;
  hasSkills: boolean;
  preview: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const analyzeReadiness = (data: any): ReadinessStats => {
  const resume = resumeToPlainText(data);
  const text = resume.toLowerCase();

  const wordCount = resume.split(/\s+/).filter(Boolean).length;

  const hasEmail = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(resume) || Boolean(data?.basics?.email);
  const hasPhone = /\+?\d[\d\s().-]{7,}\d/.test(resume) || Boolean(data?.basics?.phone);
  const contactScore = (hasEmail ? 60 : 0) + (hasPhone ? 40 : 0);

  const metrics = (resume.match(/\d+([,.]\d+)?\s?%|\$\s?\d|\b\d{2,}\b/g) ?? []).length;
  const quantScore = clamp((metrics / 5) * 100);

  const lengthScore =
    wordCount < 150
      ? clamp((wordCount / 150) * 100)
      : wordCount > 1000
        ? clamp(100 - (wordCount - 1000) / 10)
        : 100;

  const actionVerbs = ACTION_VERBS.filter((verb) => hasKeyword(text, verb)).length;
  const verbScore = clamp((actionVerbs / 6) * 100);

  const sections = data?.sections ?? {};
  const hasExperience = (sections.experience?.items?.length ?? 0) > 0;
  const hasEducation = (sections.education?.items?.length ?? 0) > 0;
  const hasSkills = (sections.skills?.items?.length ?? 0) > 0;
  const structureScore =
    (((hasExperience ? 1 : 0) + (hasEducation ? 1 : 0) + (hasSkills ? 1 : 0)) / 3) * 100;

  // No keyword category here (needs a job description); reweight the rest.
  const categories = [
    { score: contactScore, weight: 25 },
    { score: structureScore, weight: 25 },
    { score: quantScore, weight: 20 },
    { score: verbScore, weight: 15 },
    { score: lengthScore, weight: 15 },
  ];
  const score = clamp(categories.reduce((sum, c) => sum + c.score * c.weight, 0) / 100);

  return {
    score,
    wordCount,
    metrics,
    actionVerbs,
    hasEmail,
    hasPhone,
    hasExperience,
    hasEducation,
    hasSkills,
    preview: resume,
  };
};

